import { useEffect, useState } from "react";
import {
  FileText, Link2, Mail, MessageCircle, Loader2, Copy, ShieldOff, Globe, Lock, Plus, X, Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";

// ── Reports tab ──────────────────────────────────────────────────────────────
//
// Generate a report, then share it. The sharing panel is where the care goes:
// a report carries client names, deal values and revenue, and a link that
// escapes cannot be recalled.
//
// So the UI never makes the unsafe thing the easy thing. A link starts
// restricted, public access is a separate deliberate switch with its own
// warning, and revoking is one click from the row.
//
// It also never overstates what happened. WhatsApp opens with a message
// prepared — that is not delivery, and the wording says so.

const SCHEDULES = [
  { value: "daily", label: "Daily", hint: "Activities, overdue follow-ups, new clients, pipeline movement" },
  { value: "weekly", label: "Weekly", hint: "Trends, conversions, channel performance, target progress" },
  { value: "monthly", label: "Monthly", hint: "BD performance, wins and losses, projects, revenue, next month" },
  { value: "target-cycle", label: "Target Cycle", hint: "Final achievement for the cycle and the next outlook" },
];

const SavedReportsTab = ({ filters, active, isManager }: any) => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", kind: "manual", mode: "snapshot" });
  const [sharing, setSharing] = useState<any>(null);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [phone, setPhone] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [saved, sched] = await Promise.all([
        axiosInstance.get("/reports/saved"),
        axiosInstance.get("/reports/schedules").catch(() => ({ data: { schedules: [] } })),
      ]);
      setReports(saved.data.reports || []);
      setSchedules(sched.data.schedules || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not load the reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (active) load(); }, [active]);

  useEffect(() => {
    if (!sharing) return;
    axiosInstance
      .get("/messages/users")
      .then(({ data }) =>
        setRecipients((data || []).filter((u: any) => ["Admin", "Supervisor", "Sales Director"].includes(u.role)))
      )
      .catch(() => setRecipients([]));
  }, [sharing]);

  // Turning a schedule on is what makes a report arrive without anyone
  // asking. The filters currently set are what it will run with.
  const toggleSchedule = async (kind: string, on: boolean) => {
    setBusy(`sched-${kind}`);
    try {
      const { data } = await axiosInstance.post("/reports/schedules", {
        kind,
        filters,
        active: on,
        recipients: [],
      });
      toast.success(data.message);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not save the schedule.");
    } finally {
      setBusy("");
    }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await axiosInstance.post("/reports/schedules/run");
      toast.success(data.message);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not run the schedules.");
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    setBusy("generate");
    try {
      const { data } = await axiosInstance.post("/reports/generate", { ...form, ...filters });
      toast.success(data.message || "Report generated.");
      setCreating(false);
      setForm({ title: "", kind: "manual", mode: "snapshot" });
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not generate the report.");
    } finally {
      setBusy("");
    }
  };

  const makeLink = async (id: string, isPublic: boolean, days: number | null) => {
    setBusy(`link-${id}`);
    try {
      const { data } = await axiosInstance.post(`/reports/saved/${id}/link`, {
        public: isPublic,
        ...(days ? { expiresInDays: days } : {}),
      });
      toast.success(data.message);
      await load();
      setSharing((s: any) => (s && s.id === id ? { ...s, link: data.link } : s));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not create the link.");
    } finally {
      setBusy("");
    }
  };

  const revoke = async (id: string) => {
    setBusy(`revoke-${id}`);
    try {
      const { data } = await axiosInstance.delete(`/reports/saved/${id}/link`);
      toast.success(data.message);
      await load();
      setSharing((s: any) => (s && s.id === id ? { ...s, link: null } : s));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not revoke the link.");
    } finally {
      setBusy("");
    }
  };

  const sendEmail = async (id: string) => {
    if (!picked.length) { toast.error("Choose at least one recipient."); return; }
    setBusy("email");
    try {
      const { data } = await axiosInstance.post(`/reports/saved/${id}/share`, { channel: "email", recipients: picked });
      // The server reports per recipient, so a partial send is described as
      // one rather than as a success or a failure.
      toast.success(data.message);
      setPicked([]);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not send the report.");
    } finally {
      setBusy("");
    }
  };

  const sendWhatsApp = async (id: string) => {
    if (!phone.trim()) { toast.error("Enter the number to send it to."); return; }
    setBusy("whatsapp");
    try {
      const { data } = await axiosInstance.post(`/reports/saved/${id}/share`, { channel: "whatsapp", phone });
      window.open(data.url, "_blank", "noopener");
      toast(data.message, { icon: "💬", duration: 6000 });
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not open WhatsApp.");
    } finally {
      setBusy("");
    }
  };

  if (!active) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[13px] font-bold text-slate-800">Saved reports</p>
          <p className="text-[11.5px] text-slate-500">
            Generated from the filters above. A snapshot keeps its figures; a live report recalculates each time
            it is opened.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="px-3.5 py-2 rounded-lg bg-[#2462b2] text-white text-[13px] font-bold flex items-center gap-1.5"
        >
          <Plus size={14} /> Generate Report
        </button>
      </div>

      {/* Scheduling. A switched-on schedule generates on its own, once per
          period — so the card says what it will produce next rather than
          leaving "is this working?" unanswerable until the period ends. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[13px] font-bold text-slate-800">Reporting schedules</p>
            <p className="text-[11.5px] text-slate-500">
              A schedule runs with the filters set above and generates once per period, whether or not anyone is
              looking.
            </p>
          </div>
          <button type="button" onClick={runNow} disabled={busy === "run"}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {busy === "run" ? "Checking…" : "Run anything due now"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          {SCHEDULES.map((s) => {
            const live = schedules.find((x: any) => x.kind === s.value);
            const on = Boolean(live?.active);
            return (
              <div key={s.value} className={`rounded-xl border p-3 ${on ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[12px] font-bold text-slate-800">{s.label}</p>
                  <label className="shrink-0 inline-flex items-center cursor-pointer" title={on ? "Switch off" : "Switch on"}>
                    <input type="checkbox" checked={on} disabled={busy === `sched-${s.value}`}
                      onChange={(e) => toggleSchedule(s.value, e.target.checked)} />
                  </label>
                </div>
                <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{s.hint}</p>
                {on && (
                  <p className="text-[10.5px] text-emerald-800 mt-1.5">
                    Next: {live.nextPeriod}
                    {live.lastRunAt && ` · last ran ${new Date(live.lastRunAt).toLocaleDateString()}`}
                    {!live.pending && " · already produced"}
                  </p>
                )}
                {live?.lastError && <p className="text-[10.5px] text-rose-700 mt-1">{live.lastError}</p>}
                <button
                  type="button"
                  onClick={() => { setForm({ title: `${s.label} report`, kind: s.value, mode: "snapshot" }); setCreating(true); }}
                  className="text-[11px] font-semibold text-[#2462b2] mt-1.5 hover:underline"
                >
                  Generate one now →
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {loading && reports.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 flex justify-center">
          <Loader2 size={18} className="animate-spin text-slate-400" />
        </div>
      )}

      {!loading && reports.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500">
          No reports yet. Set the filters above and generate one.
        </div>
      )}

      {reports.map((r) => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-slate-800 flex items-center gap-1.5">
                <FileText size={13} /> {r.title}
              </p>
              <p className="text-[11.5px] text-slate-500">
                {r.periodLabel} · {r.mode === "live" ? "live — recalculates when opened" : "snapshot — figures as generated"} ·{" "}
                {new Date(r.createdAt).toLocaleString()}
                {r.viewCount > 0 && ` · viewed ${r.viewCount}×`}
              </p>
              {r.link && (
                <p className="text-[11px] mt-1 flex items-center gap-1.5">
                  {r.link.public
                    ? <span className="text-amber-700 font-semibold flex items-center gap-1"><Globe size={10} /> Public</span>
                    : <span className="text-slate-600 flex items-center gap-1"><Lock size={10} /> Sign-in required</span>}
                  {r.link.expiresAt && (
                    <span className={r.link.expired ? "text-rose-600 font-semibold" : "text-slate-500"}>
                      · {r.link.expired ? "expired" : `expires ${new Date(r.link.expiresAt).toLocaleDateString()}`}
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => { setSharing(r); setPicked([]); setPhone(""); }}
                className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-[12px] font-semibold hover:bg-slate-200"
              >
                Share Report
              </button>
              {r.link && (
                <button
                  type="button"
                  onClick={() => revoke(r.id)}
                  disabled={busy === `revoke-${r.id}`}
                  title="Revoke the link"
                  className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                >
                  <ShieldOff size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* ── Generate ── */}
      {creating && (
        <Modal title="Generate report" onClose={() => setCreating(false)}>
          <div className="space-y-3">
            <Field label="Title">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Leave blank to use the period" className={inputCls} />
            </Field>
            <Field label="Schedule">
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls}>
                <option value="manual">Manual</option>
                {SCHEDULES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Figures">
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={inputCls}>
                <option value="snapshot">Snapshot — keep the figures as they are now</option>
                <option value="live">Live — recalculate every time it is opened</option>
              </select>
              <span className="block text-[11px] text-slate-500 mt-1">
                {form.mode === "snapshot"
                  ? "A closed month should not change because a project was edited afterwards."
                  : "Useful for a standing link. Whoever opens it is told the figures may have moved."}
              </span>
            </Field>
            <p className="text-[11.5px] text-slate-500 rounded-lg bg-slate-50 p-2.5">
              Generated with the filters set above — period, BD and source all carry across.
            </p>
          </div>
          <Footer onCancel={() => setCreating(false)} onConfirm={generate} busy={busy === "generate"} label="Generate" />
        </Modal>
      )}

      {/* ── Share ── */}
      {sharing && (
        <Modal title={`Share · ${sharing.title}`} onClose={() => setSharing(null)} wide>
          <div className="space-y-4">
            {/* A link has to exist before anything can be sent. */}
            {!sharing.link ? (
              <div className="rounded-xl border border-slate-200 p-3.5">
                <p className="text-[12.5px] font-bold text-slate-800">Create a link first</p>
                <p className="text-[11.5px] text-slate-500 mt-0.5">
                  A restricted link still needs the viewer to sign in. Choose public only if this report may be
                  read by anyone holding the URL.
                </p>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <button type="button" onClick={() => makeLink(sharing.id, false, 30)} disabled={Boolean(busy)}
                    className="px-3 py-1.5 rounded-lg bg-[#2462b2] text-white text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-50">
                    <Lock size={12} /> Restricted link (30 days)
                  </button>
                  {isManager && (
                    <button type="button" onClick={() => makeLink(sharing.id, true, 7)} disabled={Boolean(busy)}
                      className="px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-50">
                      <Globe size={12} /> Public link (7 days)
                    </button>
                  )}
                </div>
                {!isManager && (
                  <p className="text-[11px] text-slate-400 mt-1.5">Only an administrator can make a report publicly readable.</p>
                )}
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Report link</p>
                  <div className="flex items-center gap-2 mt-1">
                    <input readOnly value={sharing.link.url} className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[11.5px] text-slate-600" />
                    <button type="button"
                      onClick={() => { navigator.clipboard.writeText(sharing.link.url); toast.success("Link copied."); }}
                      className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-100">
                      <Copy size={13} />
                    </button>
                  </div>
                  <p className="text-[11px] mt-1.5 flex items-center gap-1.5">
                    {sharing.link.public
                      ? <span className="text-amber-700 font-semibold flex items-center gap-1"><Globe size={10} /> Anyone with this link can read it</span>
                      : <span className="text-slate-600 flex items-center gap-1"><Lock size={10} /> Signing in is still required</span>}
                    {sharing.link.expiresAt && <span className="text-slate-500 flex items-center gap-1"><Clock size={10} /> expires {new Date(sharing.link.expiresAt).toLocaleDateString()}</span>}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-[12.5px] font-bold text-slate-800 flex items-center gap-1.5"><Mail size={13} /> Email</p>
                    <p className="text-[11px] text-slate-500 mb-2">Administrators and sales directors only.</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {recipients.map((u: any) => (
                        <label key={u._id} className="flex items-center gap-2 text-[12px] cursor-pointer">
                          <input type="checkbox" checked={picked.includes(u._id)}
                            onChange={(e) => setPicked((p) => e.target.checked ? [...p, u._id] : p.filter((x) => x !== u._id))} />
                          <span className="text-slate-700 truncate">{u.fullName}</span>
                        </label>
                      ))}
                      {recipients.length === 0 && <p className="text-[11px] text-slate-400">No eligible recipients.</p>}
                    </div>
                    <button type="button" onClick={() => sendEmail(sharing.id)} disabled={busy === "email" || !picked.length}
                      className="mt-2 w-full px-3 py-1.5 rounded-lg bg-[#2462b2] text-white text-[12px] font-bold disabled:opacity-50">
                      {busy === "email" ? "Sending…" : `Send to ${picked.length || 0}`}
                    </button>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-[12.5px] font-bold text-slate-800 flex items-center gap-1.5"><MessageCircle size={13} /> WhatsApp</p>
                    <p className="text-[11px] text-slate-500 mb-2">
                      Opens WhatsApp with the message ready. You press send there — delivery cannot be confirmed
                      from here.
                    </p>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+92 300 1234567" className={inputCls} />
                    <button type="button" onClick={() => sendWhatsApp(sharing.id)} disabled={busy === "whatsapp" || !phone.trim()}
                      className="mt-2 w-full px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-bold disabled:opacity-50">
                      Open WhatsApp
                    </button>
                  </div>
                </div>

                <button type="button" onClick={() => revoke(sharing.id)} disabled={Boolean(busy)}
                  className="text-[12px] text-rose-600 font-semibold hover:underline flex items-center gap-1.5">
                  <ShieldOff size={12} /> Revoke this link
                </button>
              </>
            )}
          </div>
          <Footer onCancel={() => setSharing(null)} label="" cancelLabel="Close" />
        </Modal>
      )}
    </div>
  );
};

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] outline-none focus:border-[#2462b2] focus:ring-1 focus:ring-[#2462b2]/30";

const Field = ({ label, children }: any) => (
  <div>
    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</label>
    {children}
  </div>
);

const Modal = ({ title, onClose, children, wide }: any) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
    <div className={`bg-white w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[88vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 shrink-0">
        <h3 className="text-[15px] font-bold text-slate-900">{title}</h3>
        <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:bg-slate-100 rounded-full"><X size={17} /></button>
      </div>
      <div className="px-5 py-4 overflow-y-auto">{children}</div>
    </div>
  </div>
);

const Footer = ({ onCancel, onConfirm, busy, label, cancelLabel = "Cancel" }: any) => (
  <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
    <button type="button" onClick={onCancel} className="px-4 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">{cancelLabel}</button>
    {onConfirm && (
      <button type="button" onClick={onConfirm} disabled={busy} className="px-4 py-2 text-[13px] font-bold bg-[#2462b2] text-white rounded-lg disabled:opacity-50 flex items-center gap-1.5">
        {busy && <Loader2 size={13} className="animate-spin" />}{label}
      </button>
    )}
  </div>
);

export default SavedReportsTab;
